
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as ReactRouterDom from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { 
  Activity, Search, Filter, SortAsc, ChevronRight, Download, 
  Calendar, Building2, X, ChevronDown, Target 
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import Papa from 'papaparse';
import { formatDateSafe } from '../lib/utils';

const { useNavigate } = ReactRouterDom as any;

const SessionsList: React.FC = () => {
  const navigate = useNavigate();
  
  // Persistence Helpers
  const getSaved = (key: string, def: any) => {
    const val = localStorage.getItem(`sess_list_${key}`);
    if (val === null) return def;
    try { return JSON.parse(val); } catch { return val; }
  };

  const [searchTerm, setSearchTerm] = useState(() => getSaved('searchTerm', ''));
  const [engagementFilter, setEngagementFilter] = useState(() => getSaved('engagement', ''));
  const [sortField, setSortField] = useState<string>(() => getSaved('sortField', 'session_datetime'));
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => getSaved('sortDirection', 'desc'));
  const [page, setPage] = useState(() => getSaved('page', 1));
  const [pageSize, setPageSize] = useState(() => getSaved('pageSize', 30));

  // Sync to LocalStorage
  useEffect(() => { localStorage.setItem('sess_list_searchTerm', JSON.stringify(searchTerm)); }, [searchTerm]);
  useEffect(() => { localStorage.setItem('sess_list_engagement', JSON.stringify(engagementFilter)); }, [engagementFilter]);
  useEffect(() => { localStorage.setItem('sess_list_sortField', JSON.stringify(sortField)); }, [sortField]);
  useEffect(() => { localStorage.setItem('sess_list_sortDirection', JSON.stringify(sortDirection)); }, [sortDirection]);
  useEffect(() => { localStorage.setItem('sess_list_page', JSON.stringify(page)); }, [page]);
  useEffect(() => { localStorage.setItem('sess_list_pageSize', JSON.stringify(pageSize)); }, [pageSize]);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    name: 300,
    engagement: 250,
    date: 200,
    host: 250
  });

  const { data: engagementsLookup } = useQuery({ 
    queryKey: ['lookup-engagements-sess-list'], 
    queryFn: async () => {
      const { data } = await supabase.from('engagements').select('id, name').order('name');
      return (data || []).map(e => ({ id: e.id, name: e.name }));
    }
  });

  const { data: sessionsData, isLoading } = useQuery({
    queryKey: ['sessions-list', searchTerm, engagementFilter, sortField, sortDirection, page, pageSize],
    queryFn: async () => {
      let q = supabase.from('sessions').select('*, engagements(name), host_org:host_org_id(name)', { count: 'exact' });
      
      if (searchTerm) q = q.ilike('name', `%${searchTerm}%`);
      if (engagementFilter) q = q.eq('engagement_id', engagementFilter);
      
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, error, count } = await q
        .order(sortField, { ascending: sortDirection === 'asc' })
        .range(from, to);

      if (error) throw error;
      return { sessions: data || [], total: count || 0 };
    }
  });

  const sessions = sessionsData?.sessions;
  const totalCount = sessionsData?.total || 0;

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleResize = (column: string, width: number) => {
    setColumnWidths(prev => ({ ...prev, [column]: Math.max(100, width) }));
  };

  const handleExport = () => {
    if (!sessions) return;
    const data = sessions.map(s => ({
      'Session Name': s.name || 'Session',
      'Engagement': s.engagements?.name || '—',
      'Date': formatDateSafe(s.session_datetime),
      'Host Organization': s.host_org?.name || '—',
      'Headcount': s.headcount_count || 0
    }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `hfli_sessions_${new Date().toISOString().slice(0, 10)}.csv`);
    link.click();
  };

  const SortIndicator = ({ field }: { field: string }) => {
    if (sortField !== field) return <SortAsc size={10} className="ml-1 opacity-20" />;
    return sortDirection === 'asc' 
      ? <SortAsc size={10} className="ml-1 text-blue-600" /> 
      : <SortAsc size={10} className="ml-1 text-blue-600 rotate-180" />;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight">
            Sessions Registry <span className="text-slate-300 ml-1">
              {searchTerm || engagementFilter ? `(Showing ${sessions?.length || 0} of ${totalCount})` : `(${totalCount})`}
            </span>
          </h1>
          <p className="text-slate-500 font-bold text-[10px] uppercase tracking-widest mt-1">Operational Delivery & Activity Log</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleExport} icon={<Download size={18} />}>Export CSV</Button>
        </div>
      </div>

      <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50/30">
          <div className="relative">
            <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Search Sessions</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                type="text"
                placeholder="Search by name..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-[11px] outline-none focus:ring-4 focus:ring-slate-100 transition-all shadow-sm"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              />
            </div>
          </div>

          <div>
            <SearchableSelect 
              label="Engagement Filter"
              options={engagementsLookup || []}
              value={engagementFilter}
              onChange={(val) => { setEngagementFilter(val); setPage(1); }}
              placeholder="All Engagements"
            />
          </div>

          <div className="flex items-end pb-1">
            {(searchTerm || engagementFilter) && (
              <button onClick={() => { setSearchTerm(''); setEngagementFilter(''); setPage(1); }} className="text-[9px] font-black text-rose-500 uppercase tracking-widest hover:underline">
                Reset Filters
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse table-fixed">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 text-[8px] font-black uppercase tracking-[0.2em] select-none">
                <th className="px-6 py-3 relative group" style={{ width: columnWidths.name }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('name')}>
                    Session <SortIndicator field="name" />
                  </div>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    onMouseDown={(e) => {
                      const startX = e.pageX;
                      const startWidth = columnWidths.name;
                      const onMouseMove = (moveEvent: MouseEvent) => {
                        handleResize('name', startWidth + (moveEvent.pageX - startX));
                      };
                      const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                      };
                      document.addEventListener('mousemove', onMouseMove);
                      document.addEventListener('mouseup', onMouseUp);
                    }}
                  />
                </th>
                <th className="px-6 py-3 relative group" style={{ width: columnWidths.engagement }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('engagement_id')}>
                    Engagement <SortIndicator field="engagement_id" />
                  </div>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    onMouseDown={(e) => {
                      const startX = e.pageX;
                      const startWidth = columnWidths.engagement;
                      const onMouseMove = (moveEvent: MouseEvent) => {
                        handleResize('engagement', startWidth + (moveEvent.pageX - startX));
                      };
                      const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                      };
                      document.addEventListener('mousemove', onMouseMove);
                      document.addEventListener('mouseup', onMouseUp);
                    }}
                  />
                </th>
                <th className="px-6 py-3 relative group" style={{ width: columnWidths.date }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('session_datetime')}>
                    Date <SortIndicator field="session_datetime" />
                  </div>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    onMouseDown={(e) => {
                      const startX = e.pageX;
                      const startWidth = columnWidths.date;
                      const onMouseMove = (moveEvent: MouseEvent) => {
                        handleResize('date', startWidth + (moveEvent.pageX - startX));
                      };
                      const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                      };
                      document.addEventListener('mousemove', onMouseMove);
                      document.addEventListener('mouseup', onMouseUp);
                    }}
                  />
                </th>
                <th className="px-6 py-3 relative group" style={{ width: columnWidths.host }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('host_org_id')}>
                    Host Organization <SortIndicator field="host_org_id" />
                  </div>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    onMouseDown={(e) => {
                      const startX = e.pageX;
                      const startWidth = columnWidths.host;
                      const onMouseMove = (moveEvent: MouseEvent) => {
                        handleResize('host', startWidth + (moveEvent.pageX - startX));
                      };
                      const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                      };
                      document.addEventListener('mousemove', onMouseMove);
                      document.addEventListener('mouseup', onMouseUp);
                    }}
                  />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 bg-white">
              {isLoading ? (
                [1, 2, 3, 4, 5].map(i => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan={4} className="px-6 py-4"><div className="h-4 bg-slate-50 rounded w-full"></div></td>
                  </tr>
                ))
              ) : sessions?.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-20 text-center text-[10px] font-black text-slate-200 uppercase tracking-widest italic">No matching sessions found</td>
                </tr>
              ) : (
                sessions?.map((sess: any) => (
                  <tr 
                    key={sess.id} 
                    className="hover:bg-slate-50/80 transition-all group cursor-pointer"
                    onClick={() => navigate(`/sessions/${sess.id}`)}
                  >
                    <td className="px-6 py-4">
                      <span className="text-[11px] font-black text-slate-900 uppercase group-hover:text-blue-600 transition-colors break-words">{sess.name || 'Session'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Target size={12} className="text-slate-300" />
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tight truncate">{sess.engagements?.name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar size={12} className="text-slate-300" />
                        <span className="text-[10px] font-black text-slate-900 uppercase">
                          {formatDateSafe(sess.session_datetime)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Building2 size={12} className="text-slate-300" />
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest truncate">{sess.host_org?.name || '—'}</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <div className="p-6 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Records per page</span>
              <select 
                value={pageSize} 
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="bg-white border border-slate-200 rounded px-2 py-1 text-[10px] font-bold outline-none focus:ring-2 focus:ring-blue-100"
              >
                {[30, 50, 100, 500, 1000].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
              Showing {Math.min(totalCount, (page - 1) * pageSize + 1)} - {Math.min(totalCount, page * pageSize)} of {totalCount}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === 1} 
              onClick={() => setPage((p: number) => p - 1)}
              className="text-[9px] font-black uppercase tracking-widest"
            >
              Prev
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = page;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;

                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 rounded text-[10px] font-black transition-all ${page === pageNum ? 'bg-slate-900 text-[#FFDB00]' : 'bg-white border border-slate-200 text-slate-400 hover:border-slate-900 hover:text-slate-900'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === totalPages || totalPages === 0} 
              onClick={() => setPage((p: number) => p + 1)}
              className="text-[9px] font-black uppercase tracking-widest"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionsList;
